import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ShieldCheck, User } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from '../components/ui/button';
import { NAV, CLIENT_DASHBOARD } from '../constants/testIds';

const LINKS = [
  { label: 'Quality Tiers', href: '/#quality-tiers', testId: NAV.linkQuality },
  { label: 'Bulk Order', href: '/#order-form', testId: NAV.linkOrder },
  { label: 'Services', href: '/#services', testId: NAV.linkServices },
  { label: 'FAQ', href: '/faq', testId: NAV.linkFaq },
  { label: 'Blog', href: '/blog', testId: NAV.linkBlog },
  { label: 'Contact', href: '/#contact', testId: NAV.linkContact },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-brand-bg/80 border-b border-brand-border/70">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" data-testid={NAV.logo}>
          <Logo />
        </a>

        <nav className="hidden lg:flex items-center gap-6 font-mono text-xs uppercase tracking-wider text-brand-muted">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={l.testId} className="hover:text-brand-orange transition-colors">
              {l.label}
            </a>
          ))}
          <Link to="/export" data-testid={NAV.linkExportHub} className="hover:text-brand-orange transition-colors">
            Export Destinations
          </Link>
          <Link to="/request-quote" data-testid={NAV.linkWholesaleQuote} className="hover:text-brand-orange transition-colors">
            Wholesale Quote
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/login"
            data-testid={CLIENT_DASHBOARD.navLink}
            title="Client Login"
            className="text-brand-muted hover:text-brand-orange transition-colors"
          >
            <User className="w-4 h-4" />
          </Link>
          <Link
            to="/admin/login"
            data-testid="navbar-admin-login-link"
            title="Admin Login"
            className="text-brand-muted hover:text-brand-orange transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
          </Link>
          <Button
            data-testid={NAV.ctaOrder}
            asChild
            className="bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-6"
          >
            <a href="/#order-form">Request Quote</a>
          </Button>
        </div>

        <button
          data-testid={NAV.mobileMenuToggle}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-brand-text p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileMenuPanel}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden overflow-hidden border-t border-brand-border/70 bg-brand-bg"
          >
            <div className="flex flex-col gap-5 px-6 py-6 font-mono text-sm uppercase tracking-wider text-brand-muted">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="hover:text-brand-orange transition-colors">
                  {l.label}
                </a>
              ))}
              <Link to="/export" onClick={() => setOpen(false)} data-testid="mobile-nav-link-export-hub" className="hover:text-brand-orange transition-colors">
                Export Destinations
              </Link>
              <Link to="/request-quote" onClick={() => setOpen(false)} className="hover:text-brand-orange transition-colors">
                Wholesale Quote
              </Link>
              <a
                href="/#order-form"
                onClick={() => setOpen(false)}
                className="mt-2 text-center bg-brand-orange text-white font-display font-bold uppercase tracking-wide rounded-full px-6 py-2.5"
              >
                Request Quote
              </a>
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid={CLIENT_DASHBOARD.mobileNavLink}
                className="text-center text-brand-muted hover:text-brand-orange transition-colors"
              >
                Client Login
              </Link>
              <Link
                to="/admin/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-admin-login-link"
                className="text-center text-brand-muted hover:text-brand-orange transition-colors"
              >
                Admin Login
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
